import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { AdminTable, type Column } from './AdminTable'
import { Pagination } from './Pagination'
import { LoadingBar } from './LoadingBar'
import { useAdminTab } from '@/hooks/useAdminTab'
import { fetchAdminInvitationCodes, createInvitationCode } from '@/api/admin'
import type { AdminInvitationCode } from '@/types/admin'
import { filledFill, focusRing } from '@/components/ui/buttonStyles'

interface InvitationCodesTabProps {
  active: boolean
  /** Where the Generate Code button renders; inline above the table when absent. */
  actionSlot?: HTMLElement | null
}

export function InvitationCodesTab({ active, actionSlot }: InvitationCodesTabProps) {
  const { data, meta, loading, setPage, refresh } = useAdminTab(fetchAdminInvitationCodes, active)
  const [creating, setCreating] = useState(false)

  const columns: Column<AdminInvitationCode>[] = useMemo(
    () => [
      {
        key: 'code',
        label: 'Code',
        render: (c) => <span className="font-mono tracking-wider">{c.code}</span>,
      },
      {
        key: 'status',
        label: 'Status',
        render: (c) => (
          <span
            className={`inline-block rounded-full px-2 py-0.5 text-xs ${
              c.active
                ? 'bg-chip-success-bg text-chip-success-text'
                : 'bg-surface-bg text-muted'
            }`}
          >
            {c.active ? 'Active' : 'Used'}
          </span>
        ),
      },
      {
        key: 'used_by',
        label: 'Used By',
        render: (c) => c.used_by_email ?? '—',
      },
      {
        key: 'used_at',
        label: 'Used At',
        render: (c) => (c.used_at ? new Date(c.used_at).toLocaleDateString() : '—'),
      },
      {
        key: 'created',
        label: 'Created',
        render: (c) => new Date(c.created_at).toLocaleDateString(),
      },
    ],
    [],
  )

  async function handleGenerate() {
    setCreating(true)
    try {
      await createInvitationCode()
      refresh()
    } finally {
      setCreating(false)
    }
  }

  if (!active) return null

  const generateButton = (
    <button
      onClick={handleGenerate}
      disabled={creating}
      className={`rounded-md px-4 py-2 text-sm font-normal transition-colors duration-150 disabled:opacity-50 ${filledFill} ${focusRing}`}
    >
      {creating ? 'Generating...' : 'Generate Code'}
    </button>
  )

  return (
    <div>
      {actionSlot ? createPortal(generateButton, actionSlot) : <div className="mb-4 flex justify-end">{generateButton}</div>}
      {loading && <div className="mb-4"><LoadingBar /></div>}
      <AdminTable columns={columns} data={data} keyFn={(c) => c.id} />
      <Pagination meta={meta} onPageChange={setPage} />
    </div>
  )
}
